import * as _ from 'lodash-es';
import React from 'react';
import { useQuery } from '@apollo/client';
import InvitationUserSelect from './InvitationUserSelect';
import type { InvitationUserSelectOptionType } from './InvitationUserSelect';
import { gql } from '../graphql/generated';
import useAppSelector from '../app/redux/useAppSelector';
import { ExternalUserState } from './InvitationUserSelectOption';

const AUTOCOMPLETE_QUERY = gql(/** @complexity 14 */ `
  query InvitationAutocomplete {
    session: currentSession {
      organization {
        id
        externalUsers {
          name
          email
          ...InvitationUserSelect_externalUsers
        }
        invitations {
          id
          email
        }
        users(includeDeactivated: true) {
          id
          email
          deactivatedAt
          importedFrom
        }
      }
    }
  }
`);

type InvitationExternalUser = {
  state: ExternalUserState | undefined;
  name?: string | null | undefined;
  email: string;
  avatar?: string | null | undefined;
};

const useInvitationAutocompleteQuery = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const { data: { session } = {}, loading } = useQuery(AUTOCOMPLETE_QUERY);

  if (!session?.organization?.externalUsers) {
    return { loading };
  }

  const invitedEmails = new Set(
    _.compact(
      session?.organization?.invitations?.map(
        (invitation) => invitation?.email,
      ),
    ),
  );
  const deactivatedEmails = new Set(
    _.compact(
      session?.organization?.users
        ?.filter((user) => !!user?.deactivatedAt)
        .map((user) => user?.email),
    ),
  );
  const teammateEmails = new Set(
    _.compact(
      session?.organization?.users
        ?.filter((user) => !user?.deactivatedAt && !user?.importedFrom)
        .map((user) => user?.email),
    ),
  );

  const externalUsers = session.organization.externalUsers
    .map<InvitationExternalUser>((user) => {
      const email = user?.email;
      let state;
      if (email) {
        if (invitedEmails.has(email)) {
          state = ExternalUserState.Invited;
        } else if (deactivatedEmails.has(email)) {
          state = ExternalUserState.Deactivated;
        } else if (teammateEmails.has(email)) {
          state = ExternalUserState.Teammate;
        } else {
          state = ExternalUserState.Uninvited;
        }
      }
      return {
        ...user,
        state,
      };
    })
    .filter(
      ({ state, email }) =>
        state !== ExternalUserState.Deactivated && email !== currentUser.email,
    );

  return { loading, externalUsers };
};

const EXTERNAL_USER_STATE_ORDER: Array<ExternalUserState | undefined> = [
  ExternalUserState.Uninvited,
  ExternalUserState.Invited,
  ExternalUserState.Teammate,
  ExternalUserState.Deactivated,
];

interface InvitationUserSelectFieldProps {
  value: InvitationUserSelectOptionType[];
  onChange: (invites: InvitationUserSelectOptionType[]) => void;
  disabled?: boolean;
}

const InvitationUserSelectField = ({
  value,
  onChange,
  disabled,
}: InvitationUserSelectFieldProps) => {
  const { externalUsers } = useInvitationAutocompleteQuery();
  const inviteEmails = new Set(value.map(({ email }) => email));

  const currentExternalUsers = _.sortBy(
    (externalUsers ?? []).map((user) => ({
      ...user,
      state:
        user.state === ExternalUserState.Uninvited &&
        user.email &&
        inviteEmails.has(user.email)
          ? ExternalUserState.Invited
          : user.state,
    })),
    (user) => [EXTERNAL_USER_STATE_ORDER.indexOf(user.state), user.name],
  );

  return (
    <InvitationUserSelect
      value={value}
      onChange={onChange}
      externalUsers={currentExternalUsers}
      placeholder="Enter a name or email of a teammate"
      disabled={disabled}
    />
  );
};

export default InvitationUserSelectField;
